import React, { useEffect, useState } from 'react';
import { StyleSheet, View, Text } from 'react-native';
import { Viewer, SpecialZoomLevel, Plugin, RenderViewer, Icon, MinimalButton } from '@react-pdf-viewer/core';
import { PickPhotoOrDocumentResult } from '../../services/utils';
import { pageNavigationPlugin, RenderGoToPageProps } from '@react-pdf-viewer/page-navigation';
import { isString } from 'lodash';
import { fileToBase64 } from '../../utils/Utils';

export type PdfZoomedProps = {
  source: PickPhotoOrDocumentResult | string;
};

export default function PdfZoomed({ source }: PdfZoomedProps) {
  const [base64, setBase64] = useState<string | null>(null);
  const disableScrollPluginInstance = disableScrollPlugin();
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { GoToNextPage, GoToPreviousPage } = pageNavigationPluginInstance;
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);

  useEffect(() => {
    if (isString(source)) {
      setBase64(source);
    } else {
      fileToBase64(source).then(setBase64);
    }
  }, [source]);

  return base64 ? (
    <View style={styles.pdfContainer}>
      <View style={styles.horizontalContainer}>
        <GoToPreviousPage>
          {(props: RenderGoToPageProps) => (
            <MinimalButton onClick={props.onClick}>
              <Icon size={16}>
                <path d="M18.4.5,5.825,11.626a.5.5,0,0,0,0,.748L18.4,23.5" />
              </Icon>
            </MinimalButton>
          )}
        </GoToPreviousPage>
        <Viewer
          fileUrl={base64}
          plugins={[pageNavigationPluginInstance, disableScrollPluginInstance]}
          defaultScale={SpecialZoomLevel.PageWidth}
          onPageChange={(props) => {
            setPage(props.currentPage + 1);
          }}
          onDocumentLoad={(props) => {
            setTotalPage(props.doc.numPages);
          }}
        />
        <GoToNextPage>
          {(props: RenderGoToPageProps) => (
            <MinimalButton onClick={props.onClick}>
              <Icon size={16}>
                <path d="M5.651,23.5,18.227,12.374a.5.5,0,0,0,0-.748L5.651.5" />
              </Icon>
            </MinimalButton>
          )}
        </GoToNextPage>
      </View>
      <Text style={styles.pageCounter}>
        {page} / {totalPage}
      </Text>
    </View>
  ) : (
    <View style={styles.pdfContainer} />
  );
}

const styles = StyleSheet.create({
  pdfContainer: {
    backgroundColor: '#fff',
    maxWidth: 600,
    width: '100%',
    paddingTop: 10,
  },
  horizontalContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    width: '100%',
  },
  pageCounter: {
    width: '100%',
    textAlign: 'center',
  },
});

const disableScrollPlugin = (): Plugin => {
  const renderViewer = (props: RenderViewer) => {
    const { slot } = props;

    const pageWidth = props.pageSizes[0]?.pageWidth || undefined;
    const pageHeight = props.pageSizes[0]?.pageHeight || undefined;
    const pageRatio = pageWidth && pageHeight && pageHeight / pageWidth;
    const containerWidth = props.containerRef?.current?.clientWidth;
    const height = pageRatio && containerWidth && containerWidth * pageRatio;

    if (slot.attrs && slot.attrs.style) {
      slot.attrs.style = Object.assign({}, slot.attrs.style, {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      });
    }

    if (slot.subSlot && slot.subSlot.attrs && slot.subSlot.attrs.style) {
      slot.subSlot.attrs.style = Object.assign({}, slot.subSlot.attrs.style, {
        // Disable scrolling in the pages container
        overflow: 'hidden',
        maxHeight: height,
        flex: 1,
      });
    }

    return slot;
  };

  return {
    renderViewer,
  };
};
